import React, { useRef, useEffect, useMemo, useState } from "react";
import { TabContent, TabPane } from "reactstrap";
import { Toast } from "primereact/toast";
import { TabView, TabPanel } from "primereact/tabview";
import { PageLayout } from "../../components/ui";
import { createTabHeaderTemplate } from "../../components/ui/tabHeaderTemplate";
import { useMassPaymentsStore } from "../../stores/MassPaymentsStore";
import { isSuperAdmin } from "../../authGuard";
import { Banknote } from "lucide-react";

import FileSelectionScreen from "./FileSelectionScreen";
import FileProcessingScreen from "./FileProcessingScreen";
import PreviewScreen from "./PreviewScreen";
import ExecutingScreen from "./ExecutingScreen";
import LogsScreen from "./LogsScreen";
import StepIndicator from "./StepIndicator";
import HistoryTab from "./HistoryTab";
import FileDetailsModal from "./FileDetailsModal";

const SIMPLE_VALIDATION_MESSAGES = [
  'Please select a file',
  'Please select a channel',
  'Please select a merchant'
];

const STEP_GROUPS = {
  upload: ['upload'],
  uploading: ['uploading'],
  preview: ['preview'],
  execution: ['executing', 'processing', 'logs'],
  logs: ['logs']
} as const;

const isSimpleValidation = (message: string): boolean => {
  return SIMPLE_VALIDATION_MESSAGES.some(msg => message.includes(msg));
};

const shouldShowErrorRecovery = (
  currentStep: string,
  validationResult: any,
  isUploading: boolean
): boolean => {
  if (currentStep !== 'upload' || !validationResult || isUploading) {
    return false;
  }

  if (typeof validationResult === 'string' && isSimpleValidation(validationResult)) {
    return false;
  }

  return true;
};

const MassPayments: React.FC = () => {
  const toast = useRef<Toast>(null);
  const stepContainerRef = useRef<HTMLDivElement>(null);
  
  const {
    currentStep,
    activeTab,
    transactions,
    logs,
    isUploading,
    uploadProgress,
    isProcessing,
    historyItems,
    isLoadingHistory,
    selectedHistoryItem,
    validationResult,
    selectedFile,
    selectedChannel,
    selectedMerchant,
    merchants,
    processingTransactions,
    executionProgress,
    searchTerm,
    isExporting,
    exportError,
    totalAmount,
    setActiveTab,
    loadMerchants,
    loadHistory,
    setSelectedHistoryItem,
    downloadErrorLog,
    reset,
    startNew,
    setSelectedFile,
    uploadFile,
    setSelectedChannel,
    setSelectedMerchant,
    processTransactions,
    cancel,
    exportLogs,
    setSearchTerm,
    clearExportError,
    downloadLogsForFile,
    selectedHistoryMerchant,
    setSelectedHistoryMerchant
  } = useMassPaymentsStore();

  useEffect(() => {
    if (isSuperAdmin()) {
      loadMerchants();
    }
  }, [loadMerchants]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory, selectedHistoryMerchant]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  useEffect(() => {
    if (!stepContainerRef.current) return;

    const scrollTimer = setTimeout(() => {
      const stepElements = stepContainerRef.current?.querySelectorAll('.mb-4');
      if (!stepElements?.length) return;

      const lastElement = stepElements[stepElements.length - 1] as HTMLElement;
      lastElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }, 200);
    
    return () => clearTimeout(scrollTimer);
  }, [currentStep]);

  const hasTransactionErrors = useMemo(() => 
    transactions.some(t => !t.isValid || (t.errors?.length ?? 0) > 0),
    [transactions]
  );

  const showErrorRecovery = useMemo(() => 
    shouldShowErrorRecovery(currentStep, validationResult, isUploading),
    [currentStep, validationResult, isUploading]
  );

  const isInStepGroup = (group: readonly string[]) => group.includes(currentStep);

  const renderErrorRecovery = () => {
    if (!showErrorRecovery) return null;

    return (
      <div className="mb-4">
        <div className="card">
          <div className="card-body text-center">
            <div className="mb-3">
              <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '2rem' }} />
            </div>
            <h5 className="card-title text-warning">Upload Failed</h5>
            <p className="card-text text-muted mb-4">
              There was an issue with your file upload. Please try again with a new file.
            </p>
            <button className="btn btn-primary" onClick={startNew}>
              <i className="fas fa-plus mr-2" />
              Start New Payment
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderProgressiveSteps = () => {
    const steps: JSX.Element[] = [];

    // Upload step
    if (isInStepGroup(STEP_GROUPS.upload)) {
      steps.push(
        <div key="upload" className="mb-4">
          <FileSelectionScreen
            selectedFile={selectedFile}
            validationResult={validationResult}
            isUploading={isUploading}
            selectedChannel={selectedChannel}
            selectedMerchant={selectedMerchant}
            merchants={merchants}
            onFileSelect={setSelectedFile}
            onUpload={uploadFile}
            onChannelChange={setSelectedChannel}
            onMerchantChange={setSelectedMerchant}
          />
        </div>
      );
    }

    // Processing step
    if (isInStepGroup(STEP_GROUPS.uploading) || isUploading) {
      steps.push(
        <div key="processing" className="mb-4">
          <FileProcessingScreen
            uploadProgress={uploadProgress}
            isUploadComplete={uploadProgress === 100 && !isUploading}
          />
        </div>
      );
    }

    // Preview step
    if (isInStepGroup(STEP_GROUPS.preview) || transactions.length > 0) {
      steps.push(
        <div key="preview" className="mb-4">
          <PreviewScreen
            transactions={transactions}
            totalAmount={totalAmount}
            hasErrors={hasTransactionErrors}
            onConfirmExecution={processTransactions}
            onCancel={cancel}
            onDownloadErrorLog={() => {}}
            isProcessing={isProcessing}
            currentStep={currentStep}
          />
        </div>
      );
    }

    // Execution step
    if (isInStepGroup(STEP_GROUPS.execution)) {
      steps.push(
        <div key="executing" className="mb-4">
          <ExecutingScreen
            processingTransactions={processingTransactions}
            executionProgress={executionProgress}
            onCancel={cancel}
          />
        </div>
      );
    }

    // Logs step
    if (isInStepGroup(STEP_GROUPS.logs) && logs.length > 0) {
      steps.push(
        <div key="logs" className="mb-4">
          <LogsScreen
            logs={logs}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onStartNew={startNew}
            onExportLogs={exportLogs}
            onCancel={cancel}
            isExporting={isExporting}
            exportError={exportError}
            onClearExportError={clearExportError}
          />
        </div>
      );
    }

    return steps;
  };

  const tabIndex = activeTab === 'upload' ? 0 : 1;

  return (
    <PageLayout heading="Mass Payments" icon={Banknote}>
      <div className="mass-payments-container">
        <Toast ref={toast} />

        <TabView
          activeIndex={tabIndex}
          onTabChange={(e) => setActiveTab(e.index === 0 ? 'upload' : 'history')}
        >
          <TabPanel header="Upload" headerTemplate={createTabHeaderTemplate(tabIndex)}>
            <StepIndicator currentStep={currentStep} logsGenerated={logs.length > 0} />
            <div ref={stepContainerRef}>
              {renderErrorRecovery()}
              {renderProgressiveSteps()}
            </div>
          </TabPanel>

          <TabPanel header="History" headerTemplate={createTabHeaderTemplate(tabIndex)}>
            <HistoryTab
              historyItems={historyItems}
              isLoadingHistory={isLoadingHistory}
              onFileClick={setSelectedHistoryItem}
              onDownloadLogs={downloadLogsForFile}
              onDownloadErrorLog={downloadErrorLog}
              onViewErrorDetails={() => {}}
              merchants={merchants}
              selectedHistoryMerchant={selectedHistoryMerchant}
              onHistoryMerchantChange={setSelectedHistoryMerchant}
              isSuperAdmin={isSuperAdmin()}
            />
          </TabPanel>
        </TabView>

        <FileDetailsModal
          fileDetails={selectedHistoryItem}
          isOpen={!!selectedHistoryItem}
          onClose={() => setSelectedHistoryItem(null)}
          onDownloadErrorLog={downloadErrorLog}
          onDownloadLogs={downloadLogsForFile}
        />
      </div>
    </PageLayout>
  );
};

export default MassPayments;